import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Modal, Text, NumberInput, LsaCategoriesModal, i18n, tokens } from '@simplybenefits/sb-ui-library';
import { numberWithCommas } from '../../services/helpers.js';
import { setSpendingAccountMaximums, getLsaItems } from '../../actions/spendingAccounts.js';
import * as dates from "@simplybenefits/sb-dates";

class SetMaximumsModal extends PureComponent {
    state = {
        hsaAmount: 0,
        lsaAmount: 0,
        proratedHsa: 0,
        proratedLsa: 0,
        showLsaItemsModal: false
    };

    _handleHsaChange = async (value) => {
        const amountAvailable = this.props.meta.pooledAmountAvailable;

        if (value > amountAvailable) {
            value = amountAvailable;
        }

        let diff = amountAvailable - value;

        await this.setState({
            hsaAmount: value,
            lsaAmount: diff
        });

        await this._setProratedAmounts();
    };

    _handleLsaChange = async (value) => {
        const amountAvailable = this.props.meta.pooledAmountAvailable;

        if (value > amountAvailable) {
            value = amountAvailable;
        }

        const diff = amountAvailable - value;

        await this.setState({
            lsaAmount: value,
            hsaAmount: diff
        });

        await this._setProratedAmounts();
    };

    _validateForm = () => {
        let hsaAmountError = false;
        let lsaAmountError = false;

        const hsaAmount = Number(`${ this.state.hsaAmount }`.replace(',', ''));
        const lsaAmount = Number(`${ this.state.lsaAmount }`.replace(',', ''));

        if (hsaAmount + lsaAmount !== this.props.meta.pooledAmountAvailable) {
            // eslint-disable-next-line no-multi-assign
            hsaAmountError = lsaAmountError = i18n.t(tokens.labels.spendingAccounts.ensureMaximumEqualsAvailable);
        }

        this.setState({
            hsaAmountError,
            lsaAmountError
        });

        if (
            hsaAmountError ||
            lsaAmountError
        ) {
            return false;
        }

        return true;
    };

    _setMaximums = async () => {
        const isValid = await this._validateForm();

        if (!isValid) {
            return;
        }

        this.props.dispatch(setSpendingAccountMaximums(this.state.hsaAmount, this.state.lsaAmount));
    };

    _setProratedAmounts = () => {
        const proratedBy = this.props.meta.proratedBy;
        const eligibilityDate = this.props.meta.eligibilityPeriodEnds;

        const year = new Date(eligibilityDate * 1000).getFullYear();
        const month = new Date(eligibilityDate * 1000).getUTCMonth() + 1;
        const day = new Date(eligibilityDate * 1000).getUTCDate();

        if (proratedBy === 'day') {
            const dailyHsaBalance = this.state.hsaAmount / 365;
            const dailyLsaBalance = this.state.lsaAmount / 365;

            const endOfYear = dates.getStartOfDay(`${ year + 1 }-01-01`);
            const timeLeftInYear = endOfYear - dates.getStartOfDay(eligibilityDate);
            let daysLeft = Math.floor(timeLeftInYear / 86400);

            // if it's the first of the year then use the full usage
            if (month === 1 && day === 1) {
                daysLeft = 365;
            }

            this.setState({
                proratedHsa: dailyHsaBalance * daysLeft,
                proratedLsa: dailyLsaBalance * daysLeft
            });
        } else if (proratedBy === 'month') {
            const monthlyHsaBalance = this.state.hsaAmount / 12;
            const monthlyLsaBalance = this.state.lsaAmount / 12;

            let monthsLeft = 12 - month;

            // if it's the first of the month then use the full usage for that month
            if (day === 1) {
                monthsLeft += 1;
            }

            this.setState({
                proratedHsa: monthlyHsaBalance * monthsLeft,
                proratedLsa: monthlyLsaBalance * monthsLeft
            });
        } else if (proratedBy === 'quarter') {
            const quarterHsaBalance = this.state.hsaAmount / 4;
            const quarterLsaBalance = this.state.lsaAmount / 4;

            const currentQuarter = Math.floor((new Date(eligibilityDate * 1000).getUTCMonth() + 3) / 3);
            let quartersLeft = 4 - currentQuarter;

            // if it's the first of jan, apr, july and oct then give them the full quarter
            if ([ 1, 4, 7, 10 ].indexOf(month) !== -1 && day === 1) {
                quartersLeft += 1;
            }

            if (quartersLeft > 4) {
                quartersLeft = 4;
            }

            this.setState({
                proratedHsa: quarterHsaBalance * quartersLeft,
                proratedLsa: quarterLsaBalance * quartersLeft
            });
        }
    };

    render() {
        const changeYear = dates.getUTC(null, 'YYYY');
        const effDateYear = dates.getUTC(this.props.meta.eligibilityPeriodEnds, 'YYYY');

        return (
            <Modal
                testid="setSpendingAccountMaximumsModal"
                open={true}
                title={ i18n.t(tokens.titles["set spending account maximums"]) }
                primaryButton={{
                    label: i18n.t(tokens.labels.spendingAccounts.setMaximums),
                    onClick: this._setMaximums
                }}
                loader={this.props.loader}
            >
                <Text small margin="0 0 10px 0">{ i18n.t(tokens.labels.spendingAccounts.pooledSpendingAccountNote) }</Text>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Text bold small margin="0 5px 0 0">{ i18n.t(tokens.labels.spendingAccounts.amountAvailable) } </Text>
                    <Text bold small>{ `$${ numberWithCommas(this.props.meta.pooledAmountAvailable.toFixed(2)) }` }</Text>
                </div>

                <NumberInput
                    label={ i18n.t(tokens.labels.spendingAccounts.healthSpendingMaximum) }
                    prefix="$"
                    value={Number(this.state.hsaAmount)}
                    onBlur={this._onBlur}
                    onChange={(e) => this._handleHsaChange(e.target.value)}
                    error={ this.state.hsaAmountError }
                    fullWidth
                />

                <NumberInput
                    label={ i18n.t(tokens.labels.spendingAccounts.lifestyleSpendingMaximum) }  
                    prefix="$ "
                    value={Number(this.state.lsaAmount)}
                    onBlur={this._onBlur}
                    onChange={(e) => this._handleLsaChange(e.target.value)}
                    error={ this.state.lsaAmountError }
                    fullWidth
                />

                <div
                    onClick={() => this.setState({ showLsaItemsModal: true })}
                    style={{ cursor: 'pointer' }}
                >
                    <Text tiny>{ i18n.t(tokens.labels.spendingAccounts.whatAmEligibleFor) } </Text>
                </div>

                {
                    this.state.showLsaItemsModal &&
                        <LsaCategoriesModal
                            open={ this.state.showLsaItemsModal }
                            getItems={ () => this.props.dispatch(getLsaItems()) }
                            whitelabel={ this.props.whitelabel || {} }
                            close={ () => {
                                this.setState({ showLsaItemsModal: false });
                            }}
                        />
                }

                {
                    (this.props.meta.isProrated === 1 && effDateYear === changeYear) &&
                        <>
                            <Text small margin="10px 0 20px 0">
                                { i18n.t(tokens.labels.spendingAccounts.proratedPolicyNote, { proratedBy: this.props.meta.proratedBy })}
                            </Text>

                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Text bold small margin="0 5px 0 0">{ i18n.t(tokens.labels.spendingAccounts.proratedHealthSpendingBalance) }</Text>
                                <Text bold small>{ `$${ numberWithCommas(Math.ceil(this.state.proratedHsa)) }` }</Text>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Text bold small margin="0 5px 0 0">{ i18n.t(tokens.labels.spendingAccounts.proratedLifestyleSpendingBalance) }</Text>
                                <Text bold small>{ `$${ numberWithCommas(Math.ceil(this.state.proratedLsa)) }` }</Text>
                            </div>
                        </>
                }

                {
                    (this.props.meta.isQuarterlyAllowance === 1) &&
                        <Text small margin="10px 0 20px 0">
                            { i18n.t(tokens.labels.spendingAccounts.quarterlyAllowanceNote) }
                        </Text>
                }
            </Modal>
        );
    }
}

export default connect()(SetMaximumsModal);